/**
 * Whether a signed-in account has recorded the age COPPA is applied from (S54).
 *
 * `AuthContext` creates a federated user document with `termsAcceptedAt: null` and no
 * `birthDate`, and `Login` routes a first-time federated user to `/accept-terms`, which
 * writes `birthDate` and `requiresParentalConsent`.
 *
 * Nothing enforced that. `termsAcceptedAt` was read by no code anywhere, `ProtectedRoute`
 * did not check it, and a RETURNING federated user (`isNewUser === false`) went straight
 * to `/dashboard`. So a child who closed the tab on `/accept-terms` once — or typed
 * `/dashboard` — was in permanently with no age on record. `requiresConsent()` in
 * `firestore.rules` reads a missing `requiresParentalConsent` as **false**, so that
 * account was never COPPA-covered: it could publish publicly and message other children
 * with no parental consent at all.
 *
 * Deliberately fails OPEN on a read error and CLOSED only on a successful read that shows
 * the fields absent. Locking a child out because Firestore hiccuped would be its own
 * defect; "we read the document and it has no age" is a fact.
 *
 * IT ALSO SETS THE TRACKING-CONSENT LOCK (task 92), and that is deliberate rather than
 * convenient. A COPPA-covered child cannot consent to telemetry — the permission that counts is
 * a parent's — so every non-essential category has to be forced off for their session. This hook
 * is the one place in `src/` that already reads `requiresParentalConsent` from the profile, and
 * it is mounted for every authenticated route through `ProtectedRoute`. Adding a second read of
 * the same field in a second place is how this codebase ended up with three disconnected
 * mechanisms for keyboard customization (S78), so the lock is set from the read that exists.
 */

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logger } from '../utils/logger';
import { lockForChildAccount, unlockForAdultAccount } from '../utils/trackingConsent';

export type OnboardingStatus =
  /** Still reading the profile. */
  | 'loading'
  /** An age is on record. */
  | 'complete'
  /** The document was read and carries no age — onboarding was skipped. */
  | 'incomplete'
  /** The read failed, or there is no user. Treated as complete (fail open). */
  | 'unknown';

export const useOnboardingStatus = (uid: string | undefined): OnboardingStatus => {
  const [status, setStatus] = useState<OnboardingStatus>(uid ? 'loading' : 'unknown');

  useEffect(() => {
    if (!uid) {
      setStatus('unknown');
      // Signed out. Release the lock, so the next viewer of this browser — a parent, a
      // teacher — gets their own choice back rather than the child's enforced silence. On a
      // shared classroom Chromebook that is the ordinary case, not an edge one.
      unlockForAdultAccount();
      return;
    }

    let cancelled = false;
    setStatus('loading');
    // S232: locked from the moment someone is signed in, and unlocked only by a SUCCESSFUL read
    // showing an adult. The lock used to be set only after the read, so a missing profile, a
    // failed read and the read itself all left a child's telemetry on an adult's earlier consent.
    lockForChildAccount();

    const read = async () => {
      try {
        const snap = await getDoc(doc(db.value, 'users', uid));
        if (cancelled) return;

        if (!snap.exists()) {
          // A signed-in user with no profile document yet: the creating write may
          // still be in flight. Fail open rather than bouncing them mid-signup.
          setStatus('unknown');
          return;
        }

        const data = snap.data();
        // Both fields are written together by AcceptTerms and by the email
        // registration path, and the rules require them to be added together, so
        // either one missing means onboarding did not complete.
        const hasAge =
          typeof data.birthDate === 'string' &&
          data.birthDate.length > 0 &&
          typeof data.requiresParentalConsent === 'boolean';

        // S129: a Clever-rostered child has NO date of birth — Clever does not
        // supply one — and their consent rests on the district's agreement
        // (COPPA's school-consent basis), which the server now records
        // explicitly at provisioning. Without this they were bounced to
        // /accept-terms forever, contradicting `CleverCallback`'s own note that
        // there is no accept-terms detour for Clever. The decision having been
        // MADE is what completes onboarding; the date of birth is one way of
        // making it.
        const consentDecidedBySchool =
          typeof data.requiresParentalConsent === 'boolean' &&
          data.consentBasis === 'school_authorization';

        /*
         * Task 92. Strictly boolean-true: a missing flag is read as false everywhere else in
         * this product (`requiresConsent()` in the rules does, and S54 is what that cost), so
         * it would be read as false here too — and for THIS decision the safe direction is the
         * other one. An account with no age on record is treated as a child until it has one.
         */
        const covered =
          data.requiresParentalConsent === true ||
          !(typeof data.requiresParentalConsent === 'boolean');
        if (covered) {
          lockForChildAccount();
        } else {
          unlockForAdultAccount();
        }

        setStatus(hasAge || consentDecidedBySchool ? 'complete' : 'incomplete');
      } catch (error) {
        if (cancelled) return;
        // Fail open: a transient read failure must not lock a child out.
        logger.error('Could not read onboarding status:', error);
        setStatus('unknown');
      }
    };

    void read();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  return status;
};
